import { DeviceHandoffActionType } from './constants';
import { readPersistedDeviceHandoff } from './persistence';
import { warnIllegalTransition } from './transitions';
import type { DeviceHandoffEvent, DeviceHandoffState } from './types';

const INITIAL_STATE: DeviceHandoffState = { phase: 'inactive' };

function initialDeviceHandoffState(): DeviceHandoffState {
    return readPersistedDeviceHandoff();
}

function reduceInactive(state: DeviceHandoffState, event: DeviceHandoffEvent): DeviceHandoffState {
    if (event.type === DeviceHandoffActionType.INITIATE) {
        return {
            phase: 'switch_to_respondent',
            step: { type: 'authenticating' },
            target: event.target,
            gate: event.gate,
            caregiverNames: event.caregiverNames,
            activeStudySite: event.activeStudySite,
            activityLocation: event.activityLocation,
        };
    }

    warnIllegalTransition(state.phase, event.type);

    return state;
}

function reduceSwitchToRespondent(state: DeviceHandoffState, event: DeviceHandoffEvent): DeviceHandoffState {
    if (state.phase !== 'switch_to_respondent') {
        return state;
    }

    switch (event.type) {
        case DeviceHandoffActionType.START_ACTIVATION:
            if (state.step.type !== 'authenticating') {
                break;
            }

            return {
                ...state,
                step: { type: 'activating', registrationStatus: 'inProgress' },
            };
        case DeviceHandoffActionType.ACCOUNT_CREATED:
            if (state.step.type !== 'activating' || state.step.registrationStatus !== 'inProgress') {
                break;
            }

            return {
                ...state,
                target: { ...state.target, activationStatus: true },
                step: { ...state.step, registrationStatus: 'success' },
            };
        case DeviceHandoffActionType.RESPONDENT_AUTHENTICATED:
            if (state.step.type !== 'authenticating') {
                break;
            }

            return {
                phase: 'respondent_active',
                target: state.target,
                gate: state.gate,
                activeStudySite: state.activeStudySite,
                activityLocation: state.activityLocation,
            };
        case DeviceHandoffActionType.POLICY_ACCEPTANCE_REQUIRED:
            if (state.step.type !== 'authenticating' && state.step.type !== 'activating') {
                break;
            }

            return { ...state, step: { type: 'accepting_policy' } };
        case DeviceHandoffActionType.POLICY_ACCEPTED:
            if (state.step.type !== 'accepting_policy') {
                break;
            }

            return {
                phase: 'respondent_active',
                target: state.target,
                gate: state.gate,
                activeStudySite: state.activeStudySite,
                activityLocation: state.activityLocation,
            };
        case DeviceHandoffActionType.RESPONDENT_TURN_ENDED:
            if (state.step.type !== 'accepting_policy') {
                break;
            }

            return {
                phase: 'switch_to_siteuser',
                step: { type: 'authenticating' },
                target: state.target,
                activeStudySite: state.activeStudySite,
            };
        case DeviceHandoffActionType.ACTIVATION_COMPLETED:
            if (state.step.type !== 'activating') {
                break;
            }

            return {
                phase: 'respondent_active',
                target: state.target,
                gate: state.gate,
                activeStudySite: state.activeStudySite,
                activityLocation: state.activityLocation,
            };
        case DeviceHandoffActionType.REJECTED:
            if (state.step.type !== 'authenticating' && state.step.type !== 'activating') {
                break;
            }

            return { ...state, step: { type: 'rejected', reason: event.reason } };
        case DeviceHandoffActionType.RETRY:
            if (state.step.type !== 'rejected') {
                break;
            }

            return { ...state, step: { type: 'authenticating' } };
        default:
            break;
    }

    warnIllegalTransition(state.phase, event.type);

    return state;
}

function reduceRespondentActive(state: DeviceHandoffState, event: DeviceHandoffEvent): DeviceHandoffState {
    if (state.phase !== 'respondent_active') {
        return state;
    }

    if (event.type === DeviceHandoffActionType.SET_RESPONDENT_ACTIVITY_LOCATION) {
        return { ...state, activityLocation: event.activityLocation };
    }

    if (event.type === DeviceHandoffActionType.RESPONDENT_TURN_ENDED) {
        return {
            phase: 'switch_to_siteuser',
            step: { type: 'authenticating' },
            target: state.target,
            activeStudySite: state.activeStudySite,
        };
    }

    warnIllegalTransition(state.phase, event.type);

    return state;
}

function reduceSwitchToSiteuser(state: DeviceHandoffState, event: DeviceHandoffEvent): DeviceHandoffState {
    if (state.phase !== 'switch_to_siteuser') {
        return state;
    }

    switch (event.type) {
        case DeviceHandoffActionType.SITE_USER_RETURNED:
            return { phase: 'inactive' };
        case DeviceHandoffActionType.REJECTED:
            if (state.step.type !== 'authenticating' || event.reason !== 'userNotAuthorized') {
                break;
            }

            return { ...state, step: { type: 'rejected', reason: event.reason } };
        case DeviceHandoffActionType.RETRY:
            if (state.step.type !== 'rejected') {
                break;
            }

            return { ...state, step: { type: 'authenticating' } };
        default:
            break;
    }

    warnIllegalTransition(state.phase, event.type);

    return state;
}

const DEVICE_HANDOFF_EVENT_TYPES: Set<string> = new Set(Object.values(DeviceHandoffActionType));

export function deviceHandoffReducer(
    state: DeviceHandoffState = initialDeviceHandoffState(),
    event: DeviceHandoffEvent
): DeviceHandoffState {
    if (!DEVICE_HANDOFF_EVENT_TYPES.has(event.type)) {
        return state;
    }

    if (event.type === DeviceHandoffActionType.RESET) {
        return INITIAL_STATE;
    }

    if (event.type === DeviceHandoffActionType.OVERWRITE_FROM_OTHER_TAB) {
        return event.state;
    }

    switch (state.phase) {
        case 'inactive':
            return reduceInactive(state, event);
        case 'switch_to_respondent':
            return reduceSwitchToRespondent(state, event);
        case 'respondent_active':
            return reduceRespondentActive(state, event);
        case 'switch_to_siteuser':
            return reduceSwitchToSiteuser(state, event);
        default:
            return state;
    }
}
